import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';
import { getSession, type Session } from '@vitesse/base-ui/session';
import type { GatewayEntry } from '../managers/gateway-entry';
import type { Integration } from '../../core/domain/integration';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import type { BackendClient } from '../bridge/backend-client';
import { forwardState, modelStateSelectors } from '../components/model/model-forwards';

/*
 * THE OTHER HALF OF THE PLANT, which the assistant could see on a dashboard and
 * could not read.
 *
 * Integrations are the second integration API: a customer's database, broker,
 * REST service or OT platform, modeled as a read/write-by-path entity exactly
 * like a controller. The dashboard tools already let an agent PLACE a tile
 * against one — `integrationId` has been a valid tile scope for a while — so the
 * assistant could build a view onto data it had no way of looking at itself. It
 * could name a path it could not read and could not check.
 *
 * That gap is not cosmetic. Almost every question worth asking of process data
 * crosses this line: the historian knows the temperature went out of band, and
 * the MES knows which order was running. Neither answers "which product does this
 * happen on" alone.
 *
 * THREE VERBS, IN THE ORDER SOMEONE ACTUALLY WORKS. What is connected, what is in
 * it, and what a piece of it says — mirroring gateways.list → symbols.search →
 * symbols.read, because an agent that already knows one shape should not have to
 * learn another for the same job.
 *
 * NOTHING IS MATERIALIZED. A browse pages the schema and a read pages the rows;
 * neither ever pulls a whole table, which is the property that lets this point at
 * a warehouse rather than only at a toy. The row cap here is the same ceiling
 * every read tool has — enough to describe the shape, not enough to be a
 * spreadsheet. When the answer genuinely needs the whole table, that is what the
 * sandbox is for: `sandbox.run` takes the identical `integration` source and
 * lifts the cap, because it returns a number rather than the rows.
 */

export interface IntegrationToolDeps extends GatewayScope {
  backend(): BackendClient | undefined;
}

/** Enough rows to see the shape of a table — its columns, its typical values,
 *  whether the join key looks like the one you hoped. Past this a model is being
 *  asked to be a database, and `sandbox.run` is the answer instead. */
const MAX_ROWS = 50;
/** Enough schema nodes to list a schema of ordinary size in one call, bounded so
 *  a warehouse with a million tables is a page rather than a hang. */
const MAX_NODES = 200;

const noBackend: ToolOutcome = { ok: false, message: 'The backend is not ready yet — try again in a moment.' };

const INTEGRATION_PARAM = {
  type: 'string' as const,
  description: 'Which integration, by id or display name (case-insensitive) — from integrations.list.',
};

function findIntegration(entry: GatewayEntry, idOrName: string): Integration | undefined {
  const wanted = idOrName.trim().toLowerCase();
  return entry.integrations.all.find((i) => i.id.toLowerCase() === wanted || i.displayName.toLowerCase() === wanted);
}

/** The refusal for a name that matched nothing — it lists what IS there, because
 *  "not found" without the alternatives makes a model guess again rather than
 *  pick. */
function unknownIntegration(entry: GatewayEntry, asked: string): ToolOutcome {
  const known = entry.integrations.all.map((i) => i.displayName || i.id);
  return {
    ok: false,
    message: known.length
      ? `No integration called "${asked}" on this relay. It has: ${known.join(', ')}.`
      : 'This relay has no integrations configured. An administrator adds them on the Integrations page.',
  };
}

/*
 * FORWARDS: the states of a model, streamed to a table of the customer.
 *
 * A Forward is part of an integration. It reads the change stream through its
 * own durable and writes each value 1 time, from the current values on. The 2
 * tools below change 1 Forward each, through PutForward and DeleteForward, so 2
 * people who add a Forward at the same time keep both. The tools build the
 * selectors from the model id, because a model that guesses subjects gets the
 * gateway token or the machine filter wrong.
 */

/** The table that services/integrations/docs/forward-postgres.sql makes. */
const DEFAULT_FORWARD_TABLE = 'vitesse_values';

/** A model id is 1 token of a subject: no dot, no wildcard, no white space. */
const SUBJECT_TOKEN = /^[^.*>\s]+$/;

/** Why the Forward tools cannot run now, or null. The session reads the same
 *  annotation that the gateway enforces, so the 2 answers cannot differ. It
 *  fails open without a session, as each lock does: the gateway still refuses. */
function forwardsLocked(method: Parameters<Session['allowsRpc']>[0]): string | null {
  return getSession()?.allowsRpc(method) === false
    ? 'This session cannot change the Forwards of an integration: it needs the permission org:integration:create and the IT integrations capability of the plan. An administrator can grant both.'
    : null;
}

const isoOrNull = (ms: number) => (ms > 0 ? new Date(ms).toISOString() : null);

/** The Forwards of an integration, each with its live status, for a model to read. */
function forwardsOf(i: Integration) {
  return i.forwards.map((f) => {
    const st = i.forwardStatus.find((s) => s.name === f.name);
    if (!st) return { name: f.name, target: f.target, selectors: f.selectors, state: forwardState(i, st) };
    return {
      name: f.name,
      target: f.target,
      selectors: f.selectors,
      state: st.state,
      lag: st.lag,
      lastWrite: isoOrNull(st.lastWriteMs),
      ...(st.lastError ? { lastError: st.lastError, lastErrorAt: isoOrNull(st.lastErrorMs) } : {}),
    };
  });
}

export function registerIntegrationTools(deps: IntegrationToolDeps): () => void {
  const list: ClientTool = {
    name: 'integrations.list',
    description:
      "What external systems this relay is connected to — databases, brokers, REST services, OT platforms. Each is read by " +
      'path like a controller is. Start here when a question needs data that is not from a PLC: orders, batches, recipes, ' +
      'lab results, anything a customer keeps in their own systems. The `viewKind` tells you how to read it: `table` browses ' +
      'and reads rows, `topic` is a live path tree, `endpoint` is named operations. An integration that streams model states ' +
      'to a table lists its `forwards`, each with its state, lag, last write and last error.',
    mutates: false,
    // Integrations.Subscribe requires it — the roster IS that stream's snapshot.
    permission: Permission.INTEGRATION_VIEW,
    parameters: {
      type: 'object',
      properties: { gateway: GATEWAY_PARAM },
      additionalProperties: false,
    },
    run(args): ToolOutcome {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const all = gw.value.entry.integrations.all;
      if (!all.length) {
        return {
          ok: true,
          message: `${gw.value.name} has no integrations configured, so there is no external data to read from this relay.`,
          data: { gateway: gw.value.id, integrations: [] },
        };
      }
      return {
        ok: true,
        message: `${all.length} integration${all.length === 1 ? '' : 's'} on ${gw.value.name}.`,
        data: {
          gateway: gw.value.id,
          integrations: all.map((i) => ({
            id: i.id,
            name: i.displayName,
            type: i.type,
            viewKind: i.viewKind,
            status: i.status,
            summary: i.summary,
            description: i.description,
            // 0 means "not discovered yet" as often as "empty", so it is reported
            // as the count it is rather than turned into a claim about the source.
            schemaItems: i.schemaCount,
            ...(i.status === 'error' && i.errorDetail ? { error: i.errorDetail } : {}),
            ...(i.forwards.length ? { forwards: forwardsOf(i) } : {}),
          })),
        },
      };
    },
  };

  const browse: ClientTool = {
    name: 'integrations.browse',
    description:
      "What is INSIDE an integration — its tables, or one table's columns. Call it with no `table` to page the tables; call it " +
      'with a `table` to get that table\'s columns and their types. Use `search` to narrow a large schema rather than paging ' +
      'through it. Nothing is loaded until you ask for it, so this is safe against a warehouse with thousands of tables.',
    mutates: false,
    // Integrations.BrowseSchema requires it. Separate from `read` on purpose: an
    // org can let an agent see what exists without letting it read the contents.
    permission: Permission.INTEGRATION_BROWSE,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        integration: INTEGRATION_PARAM,
        table: { type: 'string', description: "A table/collection path to list the columns of. Omit to list the tables themselves." },
        search: { type: 'string', description: 'Narrow to names containing this. Matched server-side against the whole schema, not the page.' },
        limit: { type: 'number', description: `How many to return. Defaults to 100, max ${MAX_NODES}.` },
      },
      required: ['integration'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;

      const asked = String(args.integration ?? '').trim();
      const intg = findIntegration(gw.value.entry, asked);
      if (!intg) return unknownIntegration(gw.value.entry, asked);

      const parent = String(args.table ?? '').trim();
      const search = String(args.search ?? '').trim();
      const limit = Math.min(Math.max(Number(args.limit) || 100, 1), MAX_NODES);

      try {
        const page = await backend.api.integrationBrowseSchema(gw.value.id, intg.id, parent, '', search, limit);
        if (!page.nodes.length) {
          return {
            ok: true,
            message: parent
              ? `${intg.displayName} reports no columns under "${parent}"${search ? ` matching "${search}"` : ''}. Check the table path with a browse that names no table.`
              : `${intg.displayName} reports no tables${search ? ` matching "${search}"` : ''}.`,
            data: { integration: intg.id, parent, nodes: [] },
          };
        }
        // `nextCursor` non-empty means there is more. Said out loud rather than
        // silently truncating: a model that believes it has seen a whole schema
        // will conclude a table does not exist when it is simply on page two.
        const more = page.nextCursor !== '';
        return {
          ok: true,
          message:
            `${page.nodes.length} ${parent ? `column${page.nodes.length === 1 ? '' : 's'} of "${parent}"` : `table${page.nodes.length === 1 ? '' : 's'}`} ` +
            `in ${intg.displayName}${search ? ` matching "${search}"` : ''}` +
            (more ? '. There are more — narrow with `search` rather than assuming this is all of them.' : '.'),
          data: {
            integration: intg.id,
            parent,
            hasMore: more,
            nodes: page.nodes.map((n) => ({
              path: n.path,
              kind: n.kind,
              dataType: n.dataType,
              detail: n.detail,
              readable: n.readable,
              writable: n.writable,
              columns: n.childCount,
            })),
          },
        };
      } catch (e) {
        return { ok: false, message: `Could not browse ${intg.displayName}: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  const read: ClientTool = {
    name: 'integrations.read',
    description:
      'Read rows from one target inside an integration — a table, collection or named path from integrations.browse. Returns ' +
      `at most ${MAX_ROWS} rows, which is enough to see what the data looks like and not enough to compute over. When the ` +
      'answer needs the whole table — a count, a join, a distribution — use `sandbox.run` with an `integration` dataset ' +
      'instead: it loads the same rows without the cap and returns the answer rather than the data.',
    mutates: false,
    // Integrations.Read requires it, and its own proto comment names this exact
    // use: "read-only query for the AI sandbox (complex analysis)".
    permission: Permission.INTEGRATION_READ,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        integration: INTEGRATION_PARAM,
        target: { type: 'string', description: 'The table/collection/path to read, exactly as integrations.browse returned it.' },
        limit: { type: 'number', description: `How many rows. Defaults to 20, max ${MAX_ROWS}.` },
      },
      required: ['integration', 'target'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;

      const asked = String(args.integration ?? '').trim();
      const intg = findIntegration(gw.value.entry, asked);
      if (!intg) return unknownIntegration(gw.value.entry, asked);

      const target = String(args.target ?? '').trim();
      if (!target) return { ok: false, message: 'Name the target to read — `integrations.browse` returns the exact path.' };
      const limit = Math.min(Math.max(Number(args.limit) || 20, 1), MAX_ROWS);

      try {
        const page = await backend.api.integrationReadRows(gw.value.id, intg.id, target, '', limit);
        if (!page.rows.length) {
          return {
            ok: true,
            message: `"${target}" in ${intg.displayName} returned no rows. It exists and is readable; it is empty, or every row was filtered out upstream.`,
            data: { integration: intg.id, target, rows: [] },
          };
        }
        // `totalMatching` is what stops a model reasoning about 20 rows as if they
        // were the population. It is the difference between "the average is 4.2"
        // and "the average of the first twenty is 4.2".
        const total = page.totalMatching;
        const partial = total > page.rows.length || page.nextCursor !== '';
        return {
          ok: true,
          message:
            `${page.rows.length} row${page.rows.length === 1 ? '' : 's'} from "${target}" in ${intg.displayName}` +
            (partial
              ? `, out of ${total || 'more than that'} matching. These are a SAMPLE — do not compute totals or averages from them; use sandbox.run with an \`integration\` dataset for anything over the whole table.`
              : ' — the whole target.'),
          data: { integration: intg.id, target, totalMatching: total, complete: !partial, rows: page.rows },
        };
      } catch (e) {
        return { ok: false, message: `Could not read "${target}" from ${intg.displayName}: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  const addForward: ClientTool = {
    name: 'integrations.addForward',
    description:
      'Stream the states and the status of 1 model to a table of a Postgres integration, as rows. The Forward writes the ' +
      'current state of each machine at once, then each change, each value only 1 time, and it continues after a restart or ' +
      'an outage of the database. The table must exist: services/integrations/docs/forward-postgres.sql makes ' +
      `${DEFAULT_FORWARD_TABLE} and its views. Adding the same Forward again changes nothing. A Forward cannot change: ` +
      'remove it and add it again.',
    mutates: true,
    // Integrations.PutForward requires it, as Apply does: a Forward is part of the integration.
    permission: Permission.INTEGRATION_CREATE,
    unavailable: () => forwardsLocked(Integrations.method.putForward),
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        integration: INTEGRATION_PARAM,
        name: {
          type: 'string',
          description: 'A name for the Forward, unique in the integration: letters, digits, - and _, at most 64. For example "conveyor-states".',
        },
        model: { type: 'string', description: 'The model whose states and status to stream, by id or name — from models.list.' },
        table: { type: 'string', description: `The table that receives the rows. Defaults to ${DEFAULT_FORWARD_TABLE}.` },
      },
      required: ['integration', 'name', 'model'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;

      const asked = String(args.integration ?? '').trim();
      const intg = findIntegration(gw.value.entry, asked);
      if (!intg) return unknownIntegration(gw.value.entry, asked);

      const name = String(args.name ?? '').trim();
      if (!name) return { ok: false, message: 'Name the Forward: letters, digits, - and _, for example "conveyor-states".' };
      const wanted = String(args.model ?? '').trim();
      const models = gw.value.entry.models.all;
      const model = models.find((m) => m.id === wanted) ?? models.find((m) => m.name.toLowerCase() === wanted.toLowerCase());
      if (!model) {
        return {
          ok: false,
          message: `No model "${wanted}" on ${gw.value.name}.${models.length ? ` The models here are: ${models.map((m) => (m.name && m.name !== m.id ? `${m.name} (${m.id})` : m.id)).join(', ')}.` : ''}`,
        };
      }
      if (!SUBJECT_TOKEN.test(model.id)) {
        return { ok: false, message: `The id of model "${model.name}" (${model.id}) cannot name a subject, so no Forward can select its states.` };
      }
      const table = String(args.table ?? '').trim() || DEFAULT_FORWARD_TABLE;
      const forward = { name, selectors: modelStateSelectors(gw.value.id, model.id), target: table };

      try {
        const res = await backend.api.putForward(gw.value.id, intg.id, forward);
        if (!res.ok) return { ok: false, message: `Could not add the Forward "${name}" to ${intg.displayName}: ${res.error}` };
        return {
          ok: true,
          message: res.message || `Forward "${name}" was added to ${intg.displayName}.`,
          data: { integration: intg.id, model: model.id, forward },
        };
      } catch (e) {
        return { ok: false, message: `Could not add the Forward "${name}" to ${intg.displayName}: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  const removeForward: ClientTool = {
    name: 'integrations.removeForward',
    description:
      'Stop and remove 1 Forward of an integration, by its name — from the `forwards` of integrations.list. The rows that it ' +
      'wrote stay in the table. A Forward added again later with the same name starts from the current values.',
    mutates: true,
    // Integrations.DeleteForward requires it, the same as PutForward.
    permission: Permission.INTEGRATION_CREATE,
    unavailable: () => forwardsLocked(Integrations.method.deleteForward),
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        integration: INTEGRATION_PARAM,
        name: { type: 'string', description: 'The name of the Forward to remove.' },
      },
      required: ['integration', 'name'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const backend = deps.backend();
      if (!backend) return noBackend;

      const asked = String(args.integration ?? '').trim();
      const intg = findIntegration(gw.value.entry, asked);
      if (!intg) return unknownIntegration(gw.value.entry, asked);

      const name = String(args.name ?? '').trim();
      if (!name) return { ok: false, message: 'Name the Forward to remove — integrations.list shows the `forwards` of each integration.' };

      // The service decides whether the Forward exists: the roster here can be
      // a moment behind a Forward that was just added.
      try {
        const res = await backend.api.deleteForward(gw.value.id, intg.id, name);
        if (!res.ok) return { ok: false, message: `Could not remove the Forward "${name}" of ${intg.displayName}: ${res.error}` };
        return {
          ok: true,
          message: res.message || `Forward "${name}" was removed from ${intg.displayName}.`,
          data: { integration: intg.id, removed: name },
        };
      } catch (e) {
        return { ok: false, message: `Could not remove the Forward "${name}" of ${intg.displayName}: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  return registerClientTools(list, browse, read, addForward, removeForward);
}
